import { EventEmitter } from "events";

import * as _ from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { updateUniqueSegmentsIds } from "app/actions/segmentsModuleActions";
import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import WidgetFactoryService from "app/components/widget/factory/WidgetFactoryService";
/**
 * Created by vlads on 21/1/2016.
 */
import { getShouldHideSegments } from "app/components/widget/widget-config/metrics/segments/utils";
import { getIndustrieById } from "app/components/widget/widget-types/MarketAnalysis/helpers";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import { Widget } from "app/components/widget/widget-types/Widget";
import { SEGMENTS_FAMILY_NAME } from "app/components/widget/widget-utilities/segments/constants";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    checkWidgetsLoaded,
    setIsDashboardLoading,
    setWidgetAdded,
    setWidgetRemoved,
    setWidgetUpdated,
} from "app/pages/dashboard/redux/actions/CurrentDashboardActions";
import { processDashboard } from "app/pages/dashboard/SharedDashboardModal/utils";
import type { SegmentsTypeByUsage } from "app/pages/segments/analysis/constants/common";
import {
    getEnabledWidgets,
    loadCustomSegmentsMetadataForDashboard,
} from "app/pages/segments/config/segmentsConfigHelpers";
import { CUSTOM_CATEGORY_PREFIX, UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { DefaultFetchService } from "app/services/fetchService";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { SwRedux } from "app/services/SwRedux";
import { allTrackers } from "app/services/track/track";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";
import { updateWindowUser } from "single-spa/bootstrap-utils";

import DashboardSubscriptionService from "../../../pages/dashboard/DashboardSubscriptionService";
import { UserResource } from "../../../user-data/UserResource";
import { segmentsAvailableMap } from "../constants/endpoints";
import { getSegmentsIdsFromGroup, removeInitialSegmentsIds } from "../widget-wizard/components/segments/helpers";
import { INDUSTRY_FAMILY_NAME } from "../widget-wizard/constants";
import { getCustomIndustryIdFromModel } from "../widget-wizard/utils/dashboardWizardUtils";

import type { AvailableDashboardEntity, IDashboard } from "./IDashboard";

interface ICachedUserData {
    dashboards: Record<string, unknown>[];
}

export enum EUpdateType {
    window,
    snapshot,
}

interface IDashboardFamily {
    id: string;
    title: string;
}

export interface IDashboardService {
    deleteWidget(widget: IWidget<any>): void;

    editWidget(widget: IWidget<any>): void;

    addWidget(widget: IWidget<any>): void;

    getDashboardById(dashboardId: string): IDashboard;

    deleteDashboard(dashboard: IDashboard): void;

    renameDashboard(dashboard: IDashboard): void;

    updateDashboardWidgets(dashboard: IDashboard): Promise<IDashboard>;

    addDashboard(dashboard: IDashboard): Promise<IDashboard>;

    duplicateDashboard(dashboardId, newDashboardName): Promise<IDashboard>;

    bulkAddDashboards(dashboards: IDashboard[]): Promise<IDashboard[]>;

    validateDashboardTitle(dashboards: Array<any>, dashboard: any, newTitle: string): boolean;

    generateNewTitle(nameTemplate, fieldName, objList?): string;

    getUpdateType(dashboard: any): any;

    validateDashboardFamily(family: IDashboardFamily): boolean;
}

class DashboardService implements IDashboardService {
    private _dashboards: any[];

    // Sticky for lifetime: once set, prevents late hydration from overwriting mutated state. Intentionally never reset.
    private _isDirty = false;

    private _dashboard: any;

    public newVersion = false;

    public previousDeletedWidgetId: string;

    private _events;

    protected fetchService;

    title: string;

    widgets: IWidget<any>[];

    constructor(dashboards?: any) {
        const existingDashboards = window.similarweb?.config?.userData?.dashboards;
        const seed: any[] = dashboards ?? (Array.isArray(existingDashboards) ? existingDashboards : []);
        this._dashboards = _.forEach(seed, (d) => DashboardService.addDefaultsToDashboard(d, true));

        this.fetchService = DefaultFetchService.getInstance();

        // initialize events
        this._events = new EventEmitter();

        if (!dashboards) {
            void window.similarwebPromises?.dashboardsPromise
                ?.then((res: Response) => (res?.ok ? res.json() : null))
                .then((body: { data?: any[] } | null) => {
                    if (!this._isDirty) {
                        const data = body?.data ?? [];
                        this.init(data);
                        updateWindowUser("dashboards", data);
                    }
                })
                .catch((err) => {
                    Sentry.errorTracker({
                        message: "[DashboardService] hydration failed, using defaults",
                        e: err,
                        level: "warning",
                    });
                });
        }
    }

    static addDefaultsToDashboard(dashboard, shouldHandleReadOnly = false) {
        dashboard.link = "/#/dashboard/" + dashboard.id;
        dashboard.editable = true;
        dashboard.widgets = DashboardService.fixWidgetsImgSrc(dashboard.widgets);
        dashboard.isSharedByMe = DashboardService.isSharedByMe(dashboard);
        const isReadonly = shouldHandleReadOnly ? DashboardService.getIsReadonly() : shouldHandleReadOnly;
        if (isReadonly) {
            dashboard.readOnly = true;
            dashboard.editable = false;
        }
    }

    static getIsReadonly = () => swSettings.components?.Dashboard?.resources?.IsReadonly ?? false;

    static isSharedByMe(dashboard) {
        return (
            _.result(dashboard, "sharedWithAccounts", []).length > 0 ||
            _.result(dashboard, "sharedWithUsers", []).length > 0
        );
    }

    addDefaultsToSharedDashboard = (dashboard) => {
        dashboard.link = "/#/dashboard/" + dashboard.id;
        dashboard.readOnly = true;
        dashboard.editable = false;
        dashboard.isSharedWithMe = true;
        dashboard.widgets = DashboardService.fixWidgetsImgSrc(dashboard.widgets);
        dashboard.isSharedByMe = false;
        const existingDashboardIndex = this._getDashboardIndex(dashboard.id);
        existingDashboardIndex > -1
            ? (this._dashboards[existingDashboardIndex] = dashboard)
            : this._dashboards.push(dashboard);
        this._isDirty = true;
    };

    private static fixWidgetsImgSrc(widgets) {
        if (!widgets) {
            return [];
        }
        return widgets.map(function (widget) {
            let props;
            if (typeof widget.properties === "string") {
                try {
                    JSON.parse(widget.properties);
                } catch {
                    props = null;
                }
            } else {
                props = widget.properties;
            }
            try {
                props.key.map(function (k) {
                    if (k.image) {
                        if (k.image.substring(0, 2) == "//") {
                            k.image = "https:" + k.image;
                        }
                    }
                    return k;
                });
                widget.properties = props;
                return widget;
            } catch {
                return widget;
            }
        });
    }

    private _getDashboardIndex(dashboardId) {
        return _.findIndex(this._dashboards, { id: dashboardId });
    }

    private _getWidgetIndex(widgetId) {
        return _.findIndex(this._dashboard.widgets, { id: widgetId });
    }

    widgetPosMaker(widget) {
        if (widget.hasOwnProperty("pos")) {
            if (widget.pos.hasOwnProperty("minSizeX")) {
                this.newVersion = true;
            }
        }
        let metric, type;
        if (widget.hasOwnProperty("properties")) {
            metric = widget.properties.metric;
            type = widget.properties.type;
        } else if (widget.hasOwnProperty("apiParams")) {
            metric = widget.apiParams.metric;
            type = widget.apiParams.type;
        } else {
            metric = widget.metric;
            type = widget.type;
        }
        switch (type) {
            case "PieChart":
                return { maxSizeY: 1, minSizeX: 1, maxSizeX: 2, handles: ["w", "e"] };
            case "SingleMetric":
                return { maxSizeY: 1, minSizeX: 2, maxSizeX: 2, handles: ["w", "e"] };
            case "Table":
                return { maxSizeY: 2, minSizeX: 2, maxSizeX: 4, handles: ["w", "e"] };
            case "PeriodOverPeriodVisits":
                return { maxSizeY: 1, minSizeX: 1, maxSizeX: 2, handles: ["w", "e"] };
            case "SearchOverviewTotalVisits":
                return { maxSizeY: 1, minSizeX: 1, maxSizeX: 2, handles: ["w", "e"] };
            case "SearchOverviewTotalVisitsTable":
                return { maxSizeY: 1, minSizeX: 1, maxSizeX: 2, handles: ["w", "e"] };
            case "SearchOrganicPaidOverviewPieChart":
                return { maxSizeY: 1, minSizeX: 1, maxSizeX: 2, handles: ["w", "e"] };
            case "SearchOrganicPaidOverviewTable":
                return { maxSizeY: 1, minSizeX: 1, maxSizeX: 2, handles: ["w", "e"] };
            case "KeywordAnalysisTotalVisits":
                return { maxSizeY: 1, minSizeX: 1, maxSizeX: 2, handles: ["w", "e"] };
            case "TrafficGrowthComparison":
                return { maxSizeY: 1, minSizeX: 4, maxSizeX: 4, handles: ["w", "e"] };
            case "MmxChannelsGraphDashboard":
                return { maxSizeY: 2, minSizeX: 3, maxSizeX: 4, handles: ["w", "e"] };
            default:
                return { maxSizeY: 2, minSizeX: 2, maxSizeX: 4, handles: ["w", "e"] };
        }
    }

    async initDashboard(dashboardId) {
        this._isDirty = true;
        const swRedux = SwRedux.getInstance();

        this._dashboard = this._dashboards[this._getDashboardIndex(dashboardId)];
        let widgetsMigrated = 0;
        const widgetConfigComponentsIds = [];
        const widgetCollection = Promise.all(
            this._dashboard.widgets.map(async (widgetConfig) => {
                try {
                    if (widgetConfig instanceof Widget) {
                        widgetConfig = {
                            properties: widgetConfig.getProperties(),
                            pos: widgetConfig.pos,
                            id: widgetConfig.id,
                            dashboardId: widgetConfig.dashboardId,
                            segmentId: widgetConfig?.segmentId,
                            segmentsGroupId: widgetConfig?.segmentsGroupId,
                            customIndustryId: widgetConfig?.customIndustryId,
                        };
                    }
                    if (typeof widgetConfig.properties === "string") {
                        widgetConfig.properties = JSON.parse(widgetConfig.properties);
                    }
                    if (this._dashboard.editable && this.shouldHaveCustomAssetUniqueId(widgetConfig.properties)) {
                        await this.migrateCustomAssetUniqueId(widgetConfig.properties);
                        widgetsMigrated++;
                    }
                    //Align saved widget model from DB with current filters (e.g. 'ShouldGetVerifiedData').
                    let widgetProps = widgetConfig.properties;
                    if (typeof widgetProps !== "object") {
                        //The value from add-to-dashboard is stringified.
                        widgetProps = JSON.parse(widgetProps);
                    }
                    // sim-31104: fix currapted widgets with kw group
                    // replace the name of the group with the actual name from the kw groups service
                    if (widgetConfig.properties.family === "Keyword") {
                        const { id, name } = widgetConfig.properties.key[0];
                        if (id === name) {
                            const keywordGroup = await keywordGroupsQueryClient.getGroupById(id);
                            // if it's a group context and not a keyword context
                            keywordGroup?.Name && (widgetConfig.properties.key[0].name = keywordGroup.Name);
                        }
                    }
                    //SIM-26821 - if widget type from Database doesn't exist - take the defaultType from config
                    widgetProps.type = widgetSettings.getMetricWidgetType(
                        widgetProps.metric,
                        widgetProps.type,
                        widgetProps.key.length > 1,
                    );
                    widgetProps.filters = Object.assign(
                        widgetSettings.getMetricWidgetFilters(
                            widgetProps.metric,
                            widgetProps.type,
                            widgetProps.key.length > 1,
                            true,
                        ),
                        widgetConfig.properties.filters,
                    );
                    // Get the metric properties in order to get the relevant component
                    // so we can fetch its full config
                    const metricProps = widgetSettings.getMetricProperties(widgetProps.metric, widgetProps.family);

                    // APPSP-1017
                    // Check that the metric config for the current widget is still defined and if
                    // not set the widget to undefined and filter it out of this dashboard's widgets
                    // (an example of this is where the the metric no longer exists in the FE but is
                    //   still being stored in the BE with the users data)
                    if (
                        (typeof metricProps === "object" && Object.keys(metricProps).length === 0) ||
                        (typeof widgetProps.type === "object" && Object.keys(widgetProps.type).length === 0)
                    ) {
                        return;
                    }

                    widgetConfigComponentsIds.push(getComponentIds(metricProps));
                    return widgetConfig;
                } catch (e) {
                    this.onError(`Error init widget from config, widget props: ${widgetConfig.properties}`, e);
                }
            }),
        );

        const widgetConfigCollectionResolved = await widgetCollection;
        const uniqueComponentsIdsToFetch = Array.from(new Set(widgetConfigComponentsIds.flat(1)));
        await swSettings.verifyOrFetchComponentById(uniqueComponentsIdsToFetch);

        if (this._dashboard.isHasSegments) {
            swRedux.dispatch(setIsDashboardLoading(true));

            await loadCustomSegmentsMetadataForDashboard(this._dashboard.id);
            await getEnabledWidgets(this._dashboard.id);

            swRedux.dispatch(setIsDashboardLoading(false));
        }

        const widgetInstancesCollection = widgetConfigCollectionResolved.reduce((result, widgetConfig) => {
            if (widgetConfig) {
                try {
                    const newWidget = WidgetFactoryService.create(widgetConfig);
                    if (newWidget) {
                        Object.assign(newWidget.pos, this.widgetPosMaker(newWidget));
                        result.push(newWidget);
                    }
                } catch (err) {
                    Sentry.errorTracker({
                        message: "DashboardService: Error while creating widget",
                        e: err,
                        level: "error",
                        params: {
                            extra: {
                                ...(widgetConfig || {}),
                            },
                        },
                    });
                }
            }
            return result;
        }, []);
        this._dashboard.widgets = widgetInstancesCollection;

        if (widgetsMigrated) {
            await this.updateDashboardWidgets(this._dashboard);
        }

        this._dashboard.widgets = this._dashboard.widgets.filter(Boolean);
        this._dashboard = this._dashboards[this._getDashboardIndex(dashboardId)] = processDashboard(this._dashboard);

        return this._dashboard;

        // SwRedux.getInstance().dispatch<any>(
        //     setDashboardInitialized(
        //         this._dashboard.id,
        //         this._dashboard.widgets,
        //         this._dashboard.title,
        //     ),
        // );
        // SwRedux.getInstance().dispatch<any>(initDashboardWidgetsListeners(this._dashboard.widgets));
    }

    shouldHaveCustomAssetUniqueId(model) {
        if (model.key.length > 0 && model.key[0].id) {
            if (["Keyword", "Industry"].indexOf(model.family) > -1) {
                return model.key[0].id.indexOf("$*") === 0 || model.key[0].id.indexOf("*") === 0;
            }
        }
        return false;
    }

    async migrateCustomAssetUniqueId(model) {
        if (model.family === "Industry") {
            const keyName = model.key[0].name;
            const category = keyName?.startsWith(CUSTOM_CATEGORY_PREFIX) ? keyName : CUSTOM_CATEGORY_PREFIX + keyName;
            const CustomCategory = categoryService.getCategory(category);
            const CustomCategoryId = CustomCategory.categoryId;
            model.key[0].id = CustomCategoryId;
            model.key[0].category = CustomCategoryId;
        } else if (model.family === "Keyword") {
            const KeywordGroup = await KeywordsGroupUtilities.getGroupByName(model.key[0].name);
            const KeywordGroupId = KeywordGroup.Id;
            model.key[0].id = KeywordGroupId;
        }
    }

    get dashboards() {
        return this._dashboards.sort((a, b) => (a.addedTime < b.addedTime ? 1 : -1));
    }

    getFirstDashboard() {
        const myDashboards = this._dashboards.filter((d) => !d.isSharedWithMe);
        if (myDashboards[0]) {
            return myDashboards[0];
        } else {
            return this._dashboards[0];
        }
    }

    getDashboards(concat?: unknown[]) {
        const dashboardsCopy = _.cloneDeep(this.dashboards);
        if (typeof concat === "undefined") {
            return this._dashboards;
        } else {
            return dashboardsCopy.reverse().concat(concat);
        }
    }

    get dashboard() {
        return this._dashboard;
    }

    set dashboard(dashboardId) {
        this._dashboard = this._dashboards[this._getDashboardIndex(dashboardId)];
    }

    getDashboardById(dashboardId) {
        const index = this._getDashboardIndex(dashboardId);
        return index > -1 ? this.dashboards[index] : undefined;
    }

    private static getDashboardDBPresentation(dashboard) {
        const transformed: any = _.pick(dashboard, ["id", "title"]);

        transformed.widgets = dashboard.widgets.map((widget) => {
            const widgetConfig = widget?._widgetConfig;

            const segmentId = widgetConfig?.segmentId || widgetConfig?.properties?.filters?.segmentId;
            const segmentsGroupId = widgetConfig?.segmentsGroupId || widgetConfig?.properties?.filters?.segmentsGroupId;
            const industryIdFromFilters = widgetConfig?.properties?.filters?.category;

            const customIndustryId =
                widgetConfig?.customIndustryId ||
                getIndustrieById(industryIdFromFilters)?.forApi ||
                getCustomIndustryIdFromModel(widget._widgetModel?.key);

            const isCustomCategory = !!UserCustomCategoryService.getCustomAndSharedCategoryById(customIndustryId);

            return (
                widget.getDBPresentation?.() ?? {
                    id: widget.id,
                    dashboardId: widget.dashboardId,
                    pos: typeof widget.pos === "string" ? widget.pos : JSON.stringify(widget.pos),
                    properties: widget.properties === "string" ? widget.properties : JSON.stringify(widget.properties),
                    segmentId,
                    segmentsGroupId,
                    ...(isCustomCategory && { customIndustryId }),
                }
            );
        });

        return transformed;
    }

    private onError(action, reason, id = -1) {
        Sentry.errorTracker({
            message: `DashboardService: onError - ${action}`,
            e: new Error(reason),
            level: "error",
            params: {
                extra: {
                    dashboardId: id,
                },
            },
        });
        allTrackers.trackEvent(action, "submit-error-server", `Dashboard/${id}`, reason.statusText);
    }

    async deleteWidget(widget) {
        this._isDirty = true;
        try {
            await UserResource.deleteWidget(widget.id).catch((error) => {
                Sentry.error("Error occurred when deleting widget,", error);
            });

            const widgetIndex = this._getWidgetIndex(widget.id);

            if (widgetIndex > -1) {
                const redux = SwRedux.getInstance();

                this._dashboard.widgets.splice(widgetIndex, 1);

                redux.dispatch(setWidgetRemoved(widget));
                redux.dispatch(checkWidgetsLoaded());

                const widgetModel = widget.getWidgetModel();

                if (this.previousDeletedWidgetId !== widget.id && widgetModel?.family === INDUSTRY_FAMILY_NAME) {
                    const state = redux.getState();

                    const customIndustryId =
                        widget?._widgetConfig?.customIndustryId || getCustomIndustryIdFromModel(widgetModel?.key);
                    const existingUniqueSegmentIds = state?.segmentsModule?.dashboardSegmentsMeta?.UniqueSegments;
                    const industrySegmentsIds = getIndustrieById(`*${customIndustryId}`)?.segments;

                    redux.dispatch(
                        updateUniqueSegmentsIds(
                            removeInitialSegmentsIds(existingUniqueSegmentIds, industrySegmentsIds),
                        ),
                    );

                    this.previousDeletedWidgetId = widget.id;
                }

                if (this.previousDeletedWidgetId !== widget.id && widgetModel?.family === SEGMENTS_FAMILY_NAME) {
                    const state = redux.getState();

                    const selectedGroupId = widget?._widgetConfig?.properties?.filters?.segmentsGroupId;

                    const segmentsGroups = state?.segmentsModule?.dashboardSegmentsMeta?.SegmentGroups;

                    let removedWidgetSegmentsIds;

                    if (selectedGroupId && segmentsGroups) {
                        const segmentsIdsFromGroup = getSegmentsIdsFromGroup(selectedGroupId, segmentsGroups);

                        removedWidgetSegmentsIds = segmentsIdsFromGroup?.ids;
                    } else {
                        const singleSegmentIds = widget?._params?.segmentId || widget?._params?.keys;

                        removedWidgetSegmentsIds = [singleSegmentIds];
                    }

                    const existingUniqueSegmentIds = state?.segmentsModule?.dashboardSegmentsMeta?.UniqueSegments;

                    redux.dispatch(
                        updateUniqueSegmentsIds(
                            removeInitialSegmentsIds(existingUniqueSegmentIds, removedWidgetSegmentsIds),
                        ),
                    );

                    this.previousDeletedWidgetId = widget.id;
                }
            }
        } catch (ex) {
            this.onError("Delete widget", ex, widget.id);
        }
    }

    async addWidget(widget) {
        this._isDirty = true;
        Object.assign(widget.pos, this.widgetPosMaker(widget));
        try {
            const data = await UserResource.addWidget(widget);
            this._dashboard.widgets.push(WidgetFactoryService.create(data));
            // this.emit(DashboardEvents.WIDGETS_CHANGED);
            SwRedux.getInstance().dispatch(setWidgetAdded(widget));
            SwRedux.getInstance().dispatch(checkWidgetsLoaded());
        } catch (ex) {
            this.onError("Add widget", ex, this._dashboard.id);
        }
    }

    async editWidget(widget) {
        this._isDirty = true;
        widget.properties.key.map(function (key) {
            if (key.hasOwnProperty("hidden")) {
                key.hidden = false;
            }
            return key;
        });

        try {
            const data = await UserResource.updateWidget(widget);
            this._dashboard.widgets[this._getWidgetIndex(widget.id)] = WidgetFactoryService.create(data);
            SwRedux.getInstance().dispatch(setWidgetUpdated(widget));
            SwRedux.getInstance().dispatch(checkWidgetsLoaded());
        } catch (ex) {
            this.onError("Update widget", ex, widget.id);
        }
    }

    async addDashboard(dashboard) {
        this._isDirty = true;
        try {
            const data = await UserResource.addDashboard(dashboard);
            DashboardService.addDefaultsToDashboard(data);
            this._dashboards.unshift(data);
            return data;
        } catch (ex) {
            this.onError("Add dashboard", ex);
        }
    }

    async duplicateDashboard(dashboardId, newDashboardName) {
        this._isDirty = true;
        try {
            const data = await UserResource.duplicateDashboard(dashboardId, newDashboardName);
            DashboardService.addDefaultsToDashboard(data);
            this._dashboards.unshift(data);
            return data;
        } catch (ex) {
            this.onError("Duplicate dashboard", ex);
        }
    }

    /**
     * Same as duplicateDashboard but doesn't require new title
     * @param item
     * @returns {IPromise<T>}
     */
    async cloneDashboard(item) {
        let newTitle = item.title.length > 95 ? item.title.substr(0, 95) : item.title;
        newTitle = this.generateNewTitle(`${newTitle.replace(/(Copy)\s\(\d+\)$/, "$1 ")} Copy`, "title");
        return this.duplicateDashboard(item.id, newTitle);
    }

    /**
     * Same as addDashboard but doesn't require new title
     * @returns {IPromise<T>}
     */
    async addNewDashboard() {
        const newTitle = this.generateNewTitle(i18nFilter()("home.page.dashboard.new"), "title");
        return this.addDashboard({ title: newTitle });
    }

    /**
     * Creates/Updates dashboards
     * @param dashboards
     * @returns {Promise<T>}
     */
    async bulkAddDashboards(dashboards) {
        this._isDirty = true;
        try {
            const data = await UserResource.bulkAddDashboards(dashboards);
            this.addUpdateDashboards(data);
            return data;
        } catch (ex) {
            this.onError("Bulk add dashboard", ex);
        }
    }

    /**
     * Updates widgets for existing dashboard or creates new one
     * uses dashboard id to match
     *
     * @param addedUpdatedDashboards array of dashboards
     */
    addUpdateDashboards(addedUpdatedDashboards) {
        const newDashboards = _.cloneDeep(this._dashboards);
        addedUpdatedDashboards.forEach((dashboard) => {
            const index = this._getDashboardIndex(dashboard.id);
            if (index > -1) {
                newDashboards[index].widgets = dashboard.widgets;
            } else {
                DashboardService.addDefaultsToDashboard(dashboard);
                newDashboards.unshift(dashboard);
            }
        });
        this._dashboards = newDashboards;
    }

    validateDashboardTitle(dashboard, newTitle) {
        if (newTitle.trim() === "") {
            return false;
        }
        const _dashboardsWithTitle: any = _.filter(this._dashboards, {
            title: newTitle,
        });
        if (!_dashboardsWithTitle.length && newTitle.length > 0) {
            return true;
        } else {
            if (dashboard.id) {
                return _dashboardsWithTitle.length == 1 && _dashboardsWithTitle[0].id == dashboard.id;
            } else {
                return _dashboardsWithTitle.length == 0;
            }
        }
    }

    async renameDashboard(dashboard) {
        this._isDirty = true;
        try {
            const data = await UserResource.updateDashboard(dashboard);
            this._dashboards[this._getDashboardIndex(dashboard.id)].title = data.title;
            return data;
        } catch (ex) {
            this.onError("Update dashboard", ex, dashboard.id);
        }
    }

    async updateDashboardWidgets(dashboard) {
        try {
            return await UserResource.updateDashboardWidgets(DashboardService.getDashboardDBPresentation(dashboard));
        } catch (ex) {
            this.onError("Update dashboard widgets", ex, dashboard.id);
        }
    }

    async deleteDashboard(dashboard) {
        this._isDirty = true;
        try {
            const data = UserResource.deleteDashboard(dashboard);
            this._dashboards.splice(this._getDashboardIndex(dashboard.id), 1);
            return await data;
        } catch (ex) {
            this.onError("Delete dashboard", ex, dashboard.id);
        }
    }

    async getAvaliableDashboardsWithSegments(id: string, endpointType: SegmentsTypeByUsage) {
        try {
            const endpointName = segmentsAvailableMap[endpointType];

            const avaliableDashboards: AvailableDashboardEntity[] = await this.fetchService.get(
                `api/dashboards/${endpointName}/${id}`,
            );

            return avaliableDashboards;
        } catch (error) {
            this.onError("Available segments for dashboard error: ", error);
            return null;
        }
    }

    generateNewTitle(nameTemplate, fieldName, objList = this._dashboards) {
        let result = nameTemplate;
        const listTitles = _.map(objList, fieldName);
        if (_.includes(listTitles, nameTemplate)) {
            let suffix = 1;
            while (_.includes(listTitles, `${nameTemplate} (${suffix})`)) {
                suffix++;
            }
            result = `${nameTemplate} (${suffix})`;
        }
        return result;
    }

    dashboardsubscribe(id) {
        return DashboardSubscriptionService.subscribeToNotification(id).then(() => {
            this._dashboards[this._getDashboardIndex(id)].isSubscribedToNotifications = true;
        });
    }

    dashboardUnsubscribe(id) {
        return DashboardSubscriptionService.unsubscribeToNotification(id).then(() => {
            this._dashboards[this._getDashboardIndex(id)].isSubscribedToNotifications = false;
        });
    }

    getUpdateType(dashboard) {
        const isWindow = (widget) => {
            const widgetProps = widget._widgetConfig ? widget._widgetConfig.properties : widget.properties;
            const duration = widgetProps.duration;
            return duration?.slice(-1) == "d";
        };
        const isSnapshot = (widget) => {
            const widgetProps = widget._widgetConfig ? widget._widgetConfig.properties : widget.properties;
            const duration = widgetProps.duration;
            return duration?.slice(-1) == "m";
        };

        if (dashboard.widgets.some(isWindow)) {
            return EUpdateType.window;
        } else if (dashboard.widgets.some(isSnapshot)) {
            return EUpdateType.snapshot;
        } else {
            return false;
        }
    }

    validateDashboardFamily = (family: IDashboardFamily): boolean => {
        const handleMobileFamily = () => swSettings.user.hasSwAppIntelligence;
        switch (family.id) {
            case "Mobile":
                return handleMobileFamily();
            case "Segments":
                return !getShouldHideSegments();
            default:
                return true;
        }
    };

    init(dashboards: any[] = []): void {
        this._dashboards = _.forEach([...dashboards], (d) => DashboardService.addDefaultsToDashboard(d, true));
    }
}

const newDash = new DashboardService();
export default newDash;

//this is ONLY used for tests
export const dashboardServiceFactory = (dashboards?: any) => {
    return new DashboardService(dashboards);
};

export const getComponentIds = (metricProps) => {
    // fix DMI-3221, Loading settings components on demand as as part of improving startup,
    // require to include mobileWebComponent and use a dependency array (configIdDeps)
    // to handle componentFunction.
    let componentIdsToVerifyOrFetch = [metricProps.component];
    if (metricProps.totalComponent) {
        componentIdsToVerifyOrFetch.push(metricProps.totalComponent);
    }
    if (metricProps.mobileWebComponent) {
        componentIdsToVerifyOrFetch.push(metricProps.mobileWebComponent);
    }
    if (metricProps.configIdDeps) {
        componentIdsToVerifyOrFetch = componentIdsToVerifyOrFetch.concat(metricProps.configIdDeps);
    }
    return componentIdsToVerifyOrFetch;
};
